import { useEffect, useMemo, useState, useId } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowRight, CheckCircle, MapPin, Warning,
    Sparkle, ShieldCheck, FileXls, ListBullets,
} from "@phosphor-icons/react";
import { api, endpoints } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import SearchableDropdown from "@/components/SearchableDropdown";
import BatchContribution from "@/components/BatchContribution";

const ORG_TYPES = [
    "government", "industry", "academic", "journal", "ngo",
    "consultant", "independent",
];
const METHODOLOGIES = [
    "ISO 14040/44", "GHG Protocol", "PAS 2050", "EN 15804", "PEFCR",
    "IPCC Guidelines", "Other",
];
const SYSTEM_BOUNDARIES = [
    "Cradle-to-Gate", "Cradle-to-Grave", "Gate-to-Gate",
    "Cradle-to-Cradle", "Well-to-Wheel",
];
const ALLOCATION_METHODS = ["Mass", "Economic", "Energy", "System expansion", "None"];

const DRAFT_KEY = "sdx_wizard_draft_v2";

const defaultDataset = {
    submitter_name: "",
    submitter_email: "",
    organization_name: "",
    organization_type: "independent",
    value: "",
    unit: "",
    functional_unit: "1 kg",
    methodology: "ISO 14040/44",
    system_boundary: "Cradle-to-Gate",
    allocation_method: "Mass",
    technology: "",
    geography: "",
    year: new Date().getFullYear(),
    source_url: "",
    citation: "",
    doi: "",
    confidence: 85,
    data_quality: "High",
    peer_reviewed: false,
    open_licence: true,
    notes: "",
};

export default function Submit({ embedded = false }) {
    const [entryMode, setEntryMode] = useState("single");
    // Taxonomy option lists
    const [continents, setContinents] = useState([]);
    const [countries, setCountries] = useState([]);
    const [categories, setCategories] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [materialTypes, setMaterialTypes] = useState([]);
    const [processes, setProcesses] = useState([]);
    const [parameters, setParameters] = useState([]);

    // Current selections
    const [continent, setContinent] = useState(null);
    const [country, setCountry] = useState(null);
    const [category, setCategory] = useState(null);
    const [material, setMaterial] = useState(null);
    const [materialType, setMaterialType] = useState(null);
    const [process, setProcess] = useState(null);
    const [parameter, setParameter] = useState(null);

    const [dataset, setDataset] = useState(defaultDataset);
    const [duplicates, setDuplicates] = useState(null);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const [loading, setLoading] = useState({});

    // Restore + persist draft (only the dataset fields — selections would be
    // stale by the time the user comes back).
    useEffect(() => {
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const d = JSON.parse(raw);
                if (d?.dataset) setDataset({ ...defaultDataset, ...d.dataset });
            }
        } catch { /* ignore */ }
    }, []);
    useEffect(() => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ dataset }));
    }, [dataset]);

    // Cascading loads
    useEffect(() => { api.get("/geographies").then(({data}) => setCountries(data)).catch(() => toast.error("Could not load geographies. Please reload.")); }, []);
    useEffect(() => {
        if (!country) { setCategories([]); return; }
        setLoading((s) => ({ ...s, categories: true }));
        endpoints.categories(country.id).then(setCategories)
            .finally(() => setLoading((s) => ({ ...s, categories: false })));
    }, [country]);
    useEffect(() => {
        if (!category) { setMaterials([]); return; }
        setLoading((s) => ({ ...s, materials: true }));
        endpoints.materials(category.id).then(setMaterials)
            .finally(() => setLoading((s) => ({ ...s, materials: false })));
    }, [category]);
    useEffect(() => {
        if (!material) { setMaterialTypes([]); return; }
        setLoading((s) => ({ ...s, materialTypes: true }));
        endpoints.materialTypes(material.id).then(setMaterialTypes)
            .finally(() => setLoading((s) => ({ ...s, materialTypes: false })));
    }, [material]);
    useEffect(() => {
        if (!materialType) { setProcesses([]); return; }
        setLoading((s) => ({ ...s, processes: true }));
        endpoints.processes(materialType.id).then(setProcesses)
            .finally(() => setLoading((s) => ({ ...s, processes: false })));
    }, [materialType]);
    useEffect(() => {
        if (!process) { setParameters([]); return; }
        setLoading((s) => ({ ...s, parameters: true }));
        endpoints.conceptsForProcess(process.id).then(setParameters)
            .finally(() => setLoading((s) => ({ ...s, parameters: false })));
    }, [process]);

    // Auto-fill unit & geography once we know the parameter/country.
    useEffect(() => {
        if (parameter?.default_unit)
            setDataset((d) => d.unit ? d : ({ ...d, unit: parameter.default_unit }));
    }, [parameter]);
    useEffect(() => {
        if (country?.name)
            setDataset((d) => ({ ...d, geography: country.name }));
    }, [country]);

    // Cascading resets whenever a parent changes
    const pickContinent = (c) => {
        setContinent(c);
        if (!c?.is_singleton) setCountry(null);
        setCategory(null); setMaterial(null); setMaterialType(null);
        setProcess(null); setParameter(null);
    };
    const pickCountry = (c) => {
        setCountry(c); setCategory(null); setMaterial(null);
        setMaterialType(null); setProcess(null); setParameter(null);
    };
    const pickCategory = (c) => {
        setCategory(c); setMaterial(null); setMaterialType(null);
        setProcess(null); setParameter(null);
    };
    const pickMaterial = (m) => {
        setMaterial(m); setMaterialType(null); setProcess(null); setParameter(null);
    };
    const pickMaterialType = (m) => {
        setMaterialType(m); setProcess(null); setParameter(null);
    };
    const pickProcess = (p) => { setProcess(p); setParameter(null); };

    // Add-new handlers
    const addCountry = async (name) => {
        try {
            const r = await endpoints.wizardAddCountry({
                continent_id: continent.id, name,
                submitted_by_name: dataset.submitter_name,
                submitted_by_email: dataset.submitter_email,
            });
            setCountries((prev) => prev.some((item) => item.id === r.item.id) ? prev : [...prev, r.item]);
            toast.success(r.status === "existing" ? "Using existing country" : "Country added — pending admin approval");
            return r.item;
        } catch { toast.error("Failed to add country"); return null; }
    };
    const addCategory = async (name) => {
        try {
            const r = await endpoints.wizardAddCategory({
                country_id: country.id, name,
                submitted_by_name: dataset.submitter_name,
                submitted_by_email: dataset.submitter_email,
            });
            setCategories((prev) => prev.some((item) => item.id === r.item.id) ? prev : [...prev, r.item]);
            toast.success(r.status === "existing" ? "Using existing category" : "Category added — pending admin approval");
            return r.item;
        } catch { toast.error("Failed to add category"); return null; }
    };
    const addMaterial = async (name) => {
        try {
            const r = await endpoints.wizardAddMaterial({
                category_id: category.id, name,
                submitted_by_name: dataset.submitter_name,
                submitted_by_email: dataset.submitter_email,
            });
            setMaterials((prev) => prev.some((item) => item.id === r.item.id) ? prev : [...prev, r.item]);
            toast.success(r.status === "existing" ? "Using existing material" : "Material added — pending admin approval");
            return r.item;
        } catch { toast.error("Failed to add material"); return null; }
    };
    const addMaterialType = async (name) => {
        try {
            const r = await endpoints.wizardAddMaterialType({
                material_id: material.id, name,
                submitted_by_name: dataset.submitter_name,
                submitted_by_email: dataset.submitter_email,
            });
            setMaterialTypes((prev) =>
                prev.some((m) => m.id === r.item.id) ? prev : [...prev, r.item]);
            toast.success(r.status === "existing"
                ? "Using existing material type"
                : "Item added to this contribution");
            return r.item;
        } catch { toast.error("Failed to add material type"); return null; }
    };
    const addProcess = async (name) => {
        try {
            const r = await endpoints.wizardAddProcess({
                material_type_id: materialType.id, name,
                submitted_by_name: dataset.submitter_name,
                submitted_by_email: dataset.submitter_email,
            });
            setProcesses((prev) =>
                prev.some((p) => p.id === r.item.id) ? prev : [...prev, r.item]);
            toast.success(r.status === "existing"
                ? "Using existing process"
                : "Process added to this contribution");
            return r.item;
        } catch { toast.error("Failed to add process"); return null; }
    };
    const addParameter = async (name) => {
        try {
            const r = await endpoints.wizardAddParameter({
                process_id: process.id, name,
                default_unit: dataset.unit || "",
                submitted_by_name: dataset.submitter_name,
                submitted_by_email: dataset.submitter_email,
            });
            setParameters((prev) =>
                prev.some((p) => p.id === r.item.id) ? prev : [...prev, r.item]);
            toast.success(r.status === "existing"
                ? "Using existing parameter"
                : "Parameter added to this contribution");
            return r.item;
        } catch { toast.error("Failed to add parameter"); return null; }
    };

    // Validity
    const taxonomyComplete = !!(country && category && material
        && materialType && process && parameter);
    const datasetValid = (
        dataset.submitter_name.trim() &&
        dataset.submitter_email.trim() &&
        dataset.organization_name.trim() &&
        dataset.value !== "" && !isNaN(parseFloat(dataset.value)) &&
        dataset.unit.trim() &&
        dataset.source_url.trim()
    );
    const canSubmit = taxonomyComplete && datasetValid && !submitting;

    // Submit flow — check duplicates first, then post.
    const set = (k, v) => setDataset((d) => ({ ...d, [k]: v }));

    const runDuplicateCheck = async () => {
        try {
            const dup = await endpoints.wizardDuplicateCheck({
                parameter_id: parameter.id,
                value: parseFloat(dataset.value),
                unit: dataset.unit,
            });
            setDuplicates(dup);
            if (dup.count > 0) { setShowDuplicateModal(true); return true; }
        } catch { /* ignore */ }
        return false;
    };

    const submitFinal = async () => {
        if (!canSubmit) return;
        const hasDup = await runDuplicateCheck();
        if (hasDup) return; // wait for user to confirm via modal
        await doSubmit();
    };

    const doSubmit = async () => {
        setSubmitting(true);
        try {
            const r = await endpoints.wizardSubmitDataset({
                parameter_id: parameter.id,
                submitter_name: dataset.submitter_name,
                submitter_email: dataset.submitter_email,
                organization_name: dataset.organization_name,
                organization_type: dataset.organization_type,
                value: parseFloat(dataset.value),
                unit: dataset.unit,
                functional_unit: dataset.functional_unit,
                methodology: dataset.methodology,
                system_boundary: dataset.system_boundary,
                allocation_method: dataset.allocation_method,
                technology: dataset.technology,
                geography: dataset.geography,
                year: parseInt(dataset.year, 10) || new Date().getFullYear(),
                source_url: dataset.source_url,
                citation: dataset.citation,
                doi: dataset.doi,
                confidence: parseInt(dataset.confidence, 10) || 85,
                data_quality: dataset.data_quality,
                peer_reviewed: !!dataset.peer_reviewed,
                open_licence: !!dataset.open_licence,
                notes: dataset.notes,
            });
            setDone(r.item);
            localStorage.removeItem(DRAFT_KEY);
            toast.success("Submitted — pending admin review");
        } catch { toast.error("Submission failed"); }
        finally { setSubmitting(false); }
    };

    // Breadcrumb for the sticky path card
    const crumbs = useMemo(() => [
        continent && !continent.is_singleton ? continent.name : null,
        country?.name, category?.name, material?.name,
        materialType?.name, process?.name, parameter?.name,
    ].filter(Boolean), [continent, country, category, material,
                        materialType, process, parameter]);

    if (done) {
        return (
            <SuccessScreen
                done={done}
                path={{ continent, country, category, material,
                        materialType, process, parameter }}
                onReset={() => {
                    setDone(null);
                    setContinent(null); setCountry(null); setCategory(null);
                    setMaterial(null); setMaterialType(null); setProcess(null);
                    setParameter(null); setDataset(defaultDataset);
                }}
            />
        );
    }

    return (
        <div className={`${embedded ? "max-w-none px-5 md:px-7 py-6" : "max-w-[1400px] mx-auto px-6 py-8"}`}>
            {/* Header */}
            <div className="mb-6">
                <div className="label-caps mb-2 flex items-center gap-2">
                    <Sparkle size={12} weight="fill" /> Guided contribution
                </div>
                <h1 className={`font-serif font-light tracking-tight leading-tight ${embedded ? "text-3xl md:text-4xl" : "text-4xl md:text-5xl"}`}>
                    Add a sustainability dataset
                </h1>
                <p className="text-sm text-neutral-600 mt-3 max-w-2xl">
                    Pick the taxonomy path from the dropdowns, then enter the
                    dataset details. Every submission is reviewed by an administrator
                    before it appears publicly.
                </p>
            </div>

            <div className="inline-flex items-center gap-1 p-1 bg-neutral-100 border border-neutral-200 rounded-sm mb-6">
                <button
                    type="button"
                    onClick={() => setEntryMode("single")}
                    className={`px-4 py-2 text-sm rounded-sm inline-flex items-center gap-2 ${entryMode === "single" ? "bg-white text-[#4C1D95] shadow-sm font-semibold" : "text-neutral-600"}`}
                >
                    <ListBullets size={16} /> Single entry
                </button>
                <button
                    type="button"
                    onClick={() => setEntryMode("batch")}
                    className={`px-4 py-2 text-sm rounded-sm inline-flex items-center gap-2 ${entryMode === "batch" ? "bg-white text-[#4C1D95] shadow-sm font-semibold" : "text-neutral-600"}`}
                >
                    <FileXls size={16} /> Excel batch upload
                </button>
            </div>

            {entryMode === "batch" ? (
                <BatchContribution />
            ) : (
            <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                data-testid={TEST_IDS.submitForm}>
                {/* MAIN FORM */}
                <div className="lg:col-span-2 space-y-4">
                    <FormSection
                        num={1}
                        title="Category & geography"
                        lead="Choose your geography, primary category and subcategory, then add the item and its data."
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <SearchableDropdown label="Geography" value={country} options={countries} onSelect={pickCountry} placeholder="Country, continent or Global…" required />
                            <SearchableDropdown
                                label="Primary category"
                                value={category}
                                options={categories}
                                onSelect={pickCategory}
                                placeholder="Select a category…"
                                loading={loading.categories}
                                disabled={!country}
                                allowAddNew={false}
                                addNewLabel="Add new sustainability category"
                                emptyMessage="No categories yet — add the first one below."
                                required
                            />
                            <SearchableDropdown
                                label="Subcategory"
                                value={material}
                                options={materials}
                                onSelect={pickMaterial}
                                placeholder="Select a subcategory…"
                                loading={loading.materials}
                                disabled={!category}
                                allowAddNew={false}
                                addNewLabel="Add new material or sector"
                                emptyMessage="No materials yet — add the first one below."
                                required
                            />
                            <SearchableDropdown
                                label={material?.name === "Waste & End-of-Life" ? "Waste & End-of-Life category" : "Item / Type"}
                                value={materialType}
                                options={materialTypes}
                                onSelect={pickMaterialType}
                                onAddNew={addMaterialType}
                                placeholder="Select or add an item…"
                                loading={loading.materialTypes}
                                disabled={!material}
                                allowAddNew={material?.name !== "Waste & End-of-Life"}
                                addNewLabel="Add new item"
                                emptyMessage="No items yet — add the first one below."
                                required
                            />
                            <SearchableDropdown
                                label="Process / Activity"
                                value={process}
                                options={processes}
                                onSelect={pickProcess}
                                onAddNew={addProcess}
                                placeholder="Select a process…"
                                loading={loading.processes}
                                disabled={!materialType}
                                allowAddNew
                                addNewLabel="Add new process / activity"
                                emptyMessage="No processes yet — propose one below."
                                required
                            />
                            <div className="md:col-span-2">
                                <SearchableDropdown
                                    label="Parameter"
                                    value={parameter}
                                    options={parameters}
                                    onSelect={setParameter}
                                    onAddNew={addParameter}
                                    placeholder="Select a parameter (e.g. CO₂ Emissions)…"
                                    loading={loading.parameters}
                                    disabled={!process}
                                    allowAddNew
                                    addNewLabel="Add new parameter"
                                    emptyMessage="No parameters yet — add the first one below."
                                    hint="Common parameters: CO₂ Emissions, Energy Consumption, Water Consumption, NOx, SOx, PM2.5, Wastewater, Hazardous Waste."
                                    required
                                />
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        num={2}
                        title="Contributor"
                        lead="Who is submitting this dataset?"
                        disabled={!taxonomyComplete}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Text label="Your name" value={dataset.submitter_name}
                                onChange={(v) => set("submitter_name", v)} required
                                testId={TEST_IDS.submitName} />
                            <Text label="Email" type="email" value={dataset.submitter_email}
                                onChange={(v) => set("submitter_email", v)} required
                                testId={TEST_IDS.submitEmail} />
                            <Text label="Organisation" value={dataset.organization_name}
                                onChange={(v) => set("organization_name", v)} required
                                testId={TEST_IDS.wizardOrgName}
                                placeholder="Government of India, Tata Steel, MIT…" />
                            <Select label="Organisation type"
                                value={dataset.organization_type}
                                onChange={(v) => set("organization_type", v)}
                                testId={TEST_IDS.wizardOrgType}
                                options={ORG_TYPES.map((o) => ({ value: o,
                                    label: o.charAt(0).toUpperCase() + o.slice(1) }))} />
                        </div>
                    </FormSection>

                    <FormSection
                        num={3}
                        title="Value"
                        lead="The measurement itself, plus its methodology."
                        disabled={!taxonomyComplete}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Text label="Value" type="number" step="any"
                                value={dataset.value}
                                onChange={(v) => set("value", v)} required
                                testId={TEST_IDS.wizardValueInput}
                                placeholder="1.87" />
                            <Text label="Unit" value={dataset.unit}
                                onChange={(v) => set("unit", v)} required
                                testId={TEST_IDS.wizardUnitInput}
                                placeholder={parameter?.default_unit || "kg CO2e/kg"} />
                            <Text label="Functional unit" value={dataset.functional_unit}
                                onChange={(v) => set("functional_unit", v)} />
                            <Text label="Data year" type="number"
                                value={dataset.year}
                                onChange={(v) => set("year", v)} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <Select label="Methodology" value={dataset.methodology}
                                onChange={(v) => set("methodology", v)}
                                options={METHODOLOGIES.map((m) => ({ value: m, label: m }))} />
                            <Select label="System boundary" value={dataset.system_boundary}
                                onChange={(v) => set("system_boundary", v)}
                                options={SYSTEM_BOUNDARIES.map((m) => ({ value: m, label: m }))} />
                            <Select label="Allocation" value={dataset.allocation_method}
                                onChange={(v) => set("allocation_method", v)}
                                options={ALLOCATION_METHODS.map((m) => ({ value: m, label: m }))} />
                            <Text label="Technology" value={dataset.technology}
                                onChange={(v) => set("technology", v)}
                                placeholder="e.g. Basic Oxygen Furnace" />

                            <Text label="Confidence (0-100)" type="number"
                                value={dataset.confidence}
                                onChange={(v) => set("confidence", v)} />
                        </div>
                    </FormSection>

                    <FormSection
                        num={4}
                        title="Source"
                        lead="Where does this value come from?"
                        disabled={!taxonomyComplete}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Text label="Source URL" value={dataset.source_url}
                                onChange={(v) => set("source_url", v)} required
                                testId={TEST_IDS.wizardSourceUrl}
                                placeholder="https://…" />
                            <Text label="DOI" value={dataset.doi}
                                onChange={(v) => set("doi", v)} />
                            <Text label="Citation" value={dataset.citation}
                                onChange={(v) => set("citation", v)}
                                placeholder="Author (Year). Title. Journal." />
                            <div className="flex items-center gap-4 pt-6">
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={dataset.peer_reviewed}
                                        onChange={(e) => set("peer_reviewed", e.target.checked)} />
                                    Peer reviewed
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={dataset.open_licence}
                                        onChange={(e) => set("open_licence", e.target.checked)} />
                                    Open licence
                                </label>
                            </div>
                        </div>
                        <div className="mt-4">
                            <label htmlFor="contribution-notes" className="label-caps block mb-1.5">Notes</label>
                            <textarea id="contribution-notes"
                                rows={3}
                                value={dataset.notes}
                                onChange={(e) => set("notes", e.target.value)}
                                placeholder="Any context, assumptions or caveats reviewers should know."
                                className="w-full px-3 py-2 border border-neutral-300 text-sm rounded-sm outline-none focus:border-[#6B46C1]"
                            />
                        </div>
                    </FormSection>

                    {/* Submit bar */}
                    <div className="border border-neutral-200 bg-white rounded-sm px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="text-xs text-neutral-500 flex items-center gap-2">
                            <ShieldCheck size={14} className="text-yellow-700" />
                            Your complete contribution needs one admin approval before going public.
                        </div>
                        <button
                            type="button"
                            data-testid={TEST_IDS.wizardReviewSubmit}
                            onClick={submitFinal}
                            disabled={!canSubmit}
                            className="btn-primary text-sm px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {submitting ? "Submitting…" : "Submit for review"}
                            <ArrowRight size={14} />
                        </button>
                    </div>
                </div>

                {/* CONTEXT PANEL */}
                <aside className="lg:sticky lg:top-24 lg:self-start">
                    <div className="border border-neutral-200 bg-white rounded-sm">
                        <div className="px-4 py-3 border-b border-neutral-100 bg-[#FCFCFB]">
                            <div className="label-caps flex items-center gap-2">
                                <MapPin size={12} weight="fill" /> Selected path
                            </div>
                        </div>
                        <div data-testid={TEST_IDS.wizardBreadcrumb} className="p-4">
                            {crumbs.length === 0 ? (
                                <p className="text-xs text-neutral-500">
                                    Your selection will build up here as you fill the dropdowns.
                                </p>
                            ) : (
                                <ol className="space-y-2 text-sm">
                                    {crumbs.map((c, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <span className="font-mono text-[10px] text-neutral-400 mt-1">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span className="text-[#09090B] leading-snug">{c}</span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    </div>

                    {parameter && (
                        <div className="mt-4 border border-neutral-200 bg-white rounded-sm p-4">
                            <div className="label-caps mb-2">Existing evidence</div>
                            <div className="text-xs text-neutral-500 mb-3">
                                For <span className="font-medium text-black">
                                    {parameter.name}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <Kpi label="Submissions" value={parameter.submission_count ?? 0} />
                                <Kpi label="Consensus" value={`${parameter.consensus_score ?? 0}%`} />
                                <Kpi label="Median" value={parameter.median_value ?? "—"} />
                                <Kpi label="Rating"
                                    value={parameter.community_rating
                                        ? `${parameter.community_rating.toFixed(1)} ★`
                                        : "—"} />
                            </div>
                        </div>
                    )}

                    <div className="mt-4 border border-yellow-200 bg-yellow-50 rounded-sm p-4">
                        <div className="flex items-start gap-2">
                            <ShieldCheck size={14} weight="fill"
                                className="text-yellow-800 shrink-0 mt-0.5" />
                            <div className="text-[11px] text-yellow-900 leading-relaxed">
                                Your value, source and any new item, process or parameter are reviewed together as one contribution. One approval makes the complete contribution public.
                            </div>
                        </div>
                    </div>
                </aside>
            </div>

            {showDuplicateModal && duplicates && (
                <DuplicateModal
                    duplicates={duplicates}
                    onContinue={() => { setShowDuplicateModal(false); doSubmit(); }}
                    onCancel={() => setShowDuplicateModal(false)}
                />
            )}
            </>
            )}
        </div>
    );
}

// ============================================================================
// Sub-components
// ============================================================================
function FormSection({ num, title, lead, disabled, children }) {
    return (
        <div className={[
            "border rounded-sm bg-white",
            disabled ? "border-neutral-100 opacity-60" : "border-neutral-200",
        ].join(" ")}>
            <div className="px-5 pt-5 pb-3 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center text-[11px] font-semibold rounded-full bg-[#F5F3FF] text-[#6B46C1] border border-[#D6BCFA]">
                        {num}
                    </span>
                    <div>
                        <div className="font-serif text-xl leading-tight">{title}</div>
                        {lead && <div className="text-[11px] text-neutral-500 mt-0.5">{lead}</div>}
                    </div>
                </div>
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

const Text = ({ label, value, onChange, required, type = "text",
                placeholder, step, testId }) => {
    const id=useId();
    return (
    <div>
        <label htmlFor={id} className="label-caps block mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            id={id}
            data-testid={testId}
            type={type}
            step={step}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-neutral-300 text-sm rounded-sm outline-none focus:border-[#6B46C1]"
        />
    </div>
);
};
const Select = ({ label, value, onChange, options, testId }) => {
    const id=useId();
    return (
    <div>
        <label htmlFor={id} className="label-caps block mb-1.5">{label}</label>
        <select
            id={id}
            data-testid={testId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white rounded-sm outline-none focus:border-[#6B46C1]"
        >
            {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    </div>
);
};

const Kpi = ({ label, value }) => (
    <div className="border border-neutral-100 rounded-sm p-2">
        <div className="label-caps text-[9px]">{label}</div>
        <div className="font-serif text-lg mt-0.5">{value}</div>
    </div>
);

function DuplicateModal({ duplicates, onContinue, onCancel }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
            <div
                data-testid={TEST_IDS.wizardDuplicateModal}
                className="w-full max-w-lg bg-white border border-neutral-300 rounded-sm shadow-2xl">
                <div className="px-5 py-4 border-b border-neutral-200 flex items-start gap-3">
                    <div className="w-10 h-10 bg-yellow-100 border border-yellow-300 flex items-center justify-center rounded-sm shrink-0">
                        <Warning size={20} weight="fill" className="text-yellow-700" />
                    </div>
                    <div>
                        <div className="font-serif text-2xl leading-tight">
                            Similar dataset already exists
                        </div>
                        <p className="text-xs text-neutral-600 mt-1">
                            We found {duplicates.count} approved submission
                            {duplicates.count === 1 ? "" : "s"} with a value
                            within ±15% of yours. You can still submit — SustainDex supports
                            multiple values per parameter.
                        </p>
                    </div>
                </div>
                <div className="p-4 max-h-72 overflow-y-auto space-y-2">
                    {duplicates.matches.map((m) => (
                        <div key={m.id}
                            className="border border-neutral-200 rounded-sm p-3 text-sm">
                            <div className="flex justify-between items-start gap-3">
                                <span className="font-medium">{m.organization_name}</span>
                                <span className="font-mono text-xs text-neutral-500">
                                    {m.year}
                                </span>
                            </div>
                            <div className="mt-1 font-serif text-lg">
                                {m.value}{" "}
                                <span className="text-xs text-neutral-500 font-mono">
                                    {m.unit}
                                </span>
                            </div>
                            <div className="text-[11px] text-neutral-500 mt-1">
                                {m.methodology} · {m.geography}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="p-4 border-t border-neutral-200 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        data-testid={TEST_IDS.wizardDuplicateCancel}
                        onClick={onCancel}
                        className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid={TEST_IDS.wizardDuplicateContinue}
                        onClick={onContinue}
                        className="btn-primary text-sm px-4 py-2"
                    >
                        Submit another value <ArrowRight size={12} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function SuccessScreen({ done, path, onReset }) {
    return (
        <div className="max-w-2xl mx-auto px-6 py-16 text-center"
            data-testid={TEST_IDS.wizardSuccessScreen}>
            <div className="w-16 h-16 mx-auto mb-6 border border-[#6B46C1] bg-[#F5F3FF] flex items-center justify-center rounded-sm">
                <CheckCircle size={32} weight="duotone" className="text-[#6B46C1]" />
            </div>
            <div className="label-caps mb-3">Submitted</div>
            <h1 className="font-serif text-4xl font-light tracking-tight">
                Thanks — your dataset is now in the admin review queue.
            </h1>
            <p className="text-sm text-neutral-500 mt-4">
                You&apos;ll see it publicly once an administrator approves the submission.
                Any new item, process and parameter you added are included in that
                same approval. No separate approvals are needed for each step.
            </p>
            <div className="mt-6 mx-auto max-w-md border border-neutral-200 rounded-sm p-4 text-left text-sm">
                <div className="label-caps mb-2">Submission summary</div>
                <div className="text-neutral-600 leading-relaxed">
                    <b>{path.parameter?.name}</b> under {path.country?.name} →{" "}
                    {path.material?.name} → {path.materialType?.name} →{" "}
                    {path.process?.name}
                </div>
                <div className="mt-2 font-mono text-xs text-neutral-500">
                    ID: {done.id}
                </div>
            </div>
            <div className="mt-8 flex items-center justify-center gap-3">
                <button
                    type="button"
                    onClick={onReset}
                    className="btn-primary text-sm px-5 py-2.5"
                >
                    Contribute another
                </button>
                <Link to="/explore" className="btn-secondary text-sm px-5 py-2.5">
                    Back to Explorer
                </Link>
            </div>
        </div>
    );
}
